// studioMenus — what the Studio's card menu and canvas menu offer (pen
// "Studio – detail design" §4). Pure: which entries show, never their copy.
//
// Card menu (`…` on a card, or right-click), in order:
//   - Open;
//   - Rename: the name becomes its field (MF-1, studioMachine.ts);
//   - Add to module … / Add to course …: by what the card fits in (a leaf in
//     a module or a course, a module in a course, a course in nothing). The
//     smallest kind that takes it also offers a new one (the menu path for a
//     drop on another card, D14); a kind with no container to add to and no
//     new one is left out;
//   - Export: what can leave for a learning environment;
//   - Remove from canvas: always (the material stays in the Library);
//   - Delete material …: a material or a container (never a source or a
//     file: they are not materials), behind a confirmation.
// Not offered until they work: Move to … (no projects yet) and Ask the
// assistant about this (selecting a card already makes it the composer's
// context; there is no door into the composer yet).
//
// Canvas menu (right-click on the empty canvas), made at the pointer:
//   - New module here (an empty module);
//   - Add source here …: upload, and any card taken off the canvas, to put
//     back.
// Not offered until they exist: Add note, Ask the assistant here, Paste.

export type { CanvasMenuEntry, CardMenuEntry, OffCanvasCard };
export { canvasMenuEntries, cardMenuEntries };

import { exhaustSwitchCase } from "@bilda/utils";
import type { ContainerKind } from "../../app/containerFacts.js";
import {
    type ContainerCard,
    isExportable,
    type StudioCard,
} from "./studioCards.js";

type CardMenuEntry =
    | { readonly type: "open" }
    | { readonly type: "rename" }
    | {
          readonly type: "add_to";
          readonly kind: ContainerKind;
          // Offer a new container of this kind first.
          readonly offerNew: boolean;
          readonly targets: readonly ContainerCard[];
      }
    | { readonly type: "export" }
    | { readonly type: "remove_from_canvas" }
    | { readonly type: "delete" };

// What a card can go into: the kinds that take it as a part, smallest first.
const fitsIn = (card: StudioCard): readonly ContainerKind[] => {
    switch (card.type) {
        case "material":
        case "source":
        case "generic":
            return ["module", "course"];
        case "container":
            return card.kind === "module" ? ["course"] : [];
        default:
            return exhaustSwitchCase(card);
    }
};

const isMaterial = (card: StudioCard): boolean => {
    switch (card.type) {
        case "material":
        case "container":
            return true;
        case "source":
        case "generic":
            return false;
        default:
            return exhaustSwitchCase(card);
    }
};

const cardMenuEntries = (
    card: StudioCard,
    cards: readonly StudioCard[],
): readonly CardMenuEntry[] => {
    const containers = cards.filter(
        (c): c is ContainerCard => c.type === "container" && c.id !== card.id,
    );
    const addTo = fitsIn(card).flatMap((kind, i): readonly CardMenuEntry[] => {
        const targets = containers.filter((c) => c.kind === kind);
        const offerNew = i === 0;
        return offerNew || targets.length > 0
            ? [{ type: "add_to", kind, offerNew, targets }]
            : [];
    });
    const when = (
        shown: boolean,
        entry: CardMenuEntry,
    ): readonly CardMenuEntry[] => (shown ? [entry] : []);
    return [
        { type: "open" },
        { type: "rename" },
        ...addTo,
        ...when(isExportable(card), { type: "export" }),
        { type: "remove_from_canvas" },
        ...when(isMaterial(card), { type: "delete" }),
    ];
};

// A card taken off the canvas, to put back from the canvas menu.
type OffCanvasCard = {
    readonly id: string;
    readonly title: string;
};

type CanvasMenuEntry =
    | { readonly type: "new_module_here" }
    | {
          readonly type: "add_source_here";
          readonly offCanvas: readonly OffCanvasCard[];
      };

const canvasMenuEntries = (
    offCanvas: readonly OffCanvasCard[],
): readonly CanvasMenuEntry[] => [
    { type: "new_module_here" },
    { type: "add_source_here", offCanvas },
];
